import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  Tooltip,
  List,
  Checkbox,
  Box,
  Typography,
  Stack,
  Divider,
  Paper,
  Snackbar,
  Alert,
  Chip,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from "@mui/material";
import { ButtonSpinner } from "../ui/ButtonSpinner";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SchoolIcon from "@mui/icons-material/School";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import axios from "axios";

/* ---------- Highlight Search ---------- */
const highlightText = (text, search) => {
  if (!search) return text;
  const parts = text.split(new RegExp(`(${search})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === search.toLowerCase() ? (
      <mark key={i} style={{ background: "rgba(91, 84, 230, 0.16)", padding: "0 4px", borderRadius: 4 }}>
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// This dialog renders inside the Instructor MUI ThemeProvider, so it inherits the
// project's central design system (Hanken Grotesk, indigo accent, surfaces, radii,
// button styles). sx below uses theme tokens only — no hardcoded colours/fonts.
const ImportCourse = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, type: "success", msg: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
const [importOption, setImportOption] = useState("ONLY_COURSES");
const userId=localStorage.getItem('userId')
const [totalCourses, setTotalCourses] = useState(0);
const [usedCourses, setUsedCourses] = useState(0);
const [courseTypes, setCourseTypes] = useState({
  "K-12": false,
  Certification: false,
  University: false
});

  const getAuth = () => {
    const u = process.env.REACT_APP_API_USERNAME;
    const p = process.env.REACT_APP_API_PASSWORD;
    return `Basic ${btoa(`${u}:${p}`)}`;
  };

  const handleTypeChange = (type) => {
  setCourseTypes((prev) => ({
    ...prev,
    [type]: !prev[type]
  }));
};


  useEffect(() => {
    if (open) {
      fetchCourses();
      setSearch("");
      setSelected([]);
      
    }
  }, [open]);

 const fetchCourses = async () => {
  setLoading(true);
  try {
    // orgId drives per-organization catalog targeting. The backend validates it
    // against this user's memberships and falls back to their default org.
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/getCourseForImportFunction/${userId}`,
      {
        params: { orgId: localStorage.getItem("orgId") },
        headers: { Authorization: getAuth() },
      }
    );

    // 🔹 NEW response handling
    setCourses(res.data.courses || []);
    setTotalCourses(res.data.totalCourses || 0);
    setUsedCourses(res.data.usedCourses || 0);

  } catch {
    setSnackbar({ open: true, type: "error", msg: "Failed to load courses" });
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filteredCourses = useMemo(() => {
  const activeTypes = Object.keys(courseTypes).filter(
    (type) => courseTypes[type]
  );

  return courses.filter((course) => {
    const matchesSearch = course.courseName
      ?.toLowerCase()
      .includes(debouncedSearch.toLowerCase());

    const matchesType =
      activeTypes.length === 0 ||
      activeTypes.includes(course.courseType);

    return matchesSearch && matchesType;
  });
}, [courses, debouncedSearch, courseTypes]);


  const toggle = (id) => {
  const isAlreadySelected = selected.includes(id);

  // ✅ Allow unselect always
  if (isAlreadySelected) {
    setSelected((prev) => prev.filter((i) => i !== id));
    return;
  }

  // 🚫 Block selection if limit reached. totalCourses < 0 means an unlimited plan
  // (e.g. an invited instructor on the org's shared Organization tier) — never block.
  if (totalCourses >= 0 && usedCourses + selected.length >= totalCourses) {
    setSnackbar({
      open: true,
      type: "error",
      msg: "Course limit reached. Upgrade your plan to add more courses."
    });
    return;
  }

  // ✅ Select course
  setSelected((prev) => [...prev, id]);
};


  const handleImport = async () => {
    setImporting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/Copy-PreDefine-courses`,
        // orgId = the portal's active org, so multi-org users import into the
        // org they are currently working in (backend falls back to default org).
        { userId, courseIds: selected, option: importOption, orgId: localStorage.getItem("orgId") },
        { headers: { Authorization: getAuth() } }
      );
      setSnackbar({ open: true, type: "success", msg: "Courses imported successfully" });
      setConfirmOpen(false);
      onClose();
      // Brief pause so the success snackbar is visible before the course list
      // refreshes. Reload only on success — reloading on failure wiped the
      // error message before the user could read it.
      setTimeout(() => window.location.reload(), 1200);
    } catch {
      setSnackbar({ open: true, type: "error", msg: "Import failed" });
      setConfirmOpen(false);
    } finally {
      setImporting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }} fullWidth maxWidth="sm">
        {/* ---------- Header ---------- */}
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <CloudDownloadIcon fontSize="small" color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ display: "block" }}>Import Courses</Box>
            <Typography sx={{ mt: 0.25, fontSize: 12.5, fontWeight: 500, color: "text.secondary" }}>
              Select ready-made courses and clone them into your organization.
            </Typography>
          </Box>
        </DialogTitle>

        {/* ---------- Search + List ---------- */}
        <DialogContent dividers>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems="stretch"
            sx={{ mb: 1.5 }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search courses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                p: 0.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper"
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flex: "0 0 auto",
                  borderRadius: "8px",
                  display: "grid",
                  placeItems: "center",
                  color: "primary.main",
                  bgcolor: "action.selected"
                }}
              >
                <FilterAltIcon fontSize="small" />
              </Box>
              {["K-12", "Certification", "University"].map((type) => (
                <Tooltip key={type} title={type} arrow placement="top">
                  <FormControlLabel
                    sx={{
                      m: 0,
                      px: 0.6,
                      flex: "1 1 0",
                      justifyContent: "center",
                      borderRadius: "999px",
                      border: "1px solid",
                      borderColor: courseTypes[type] ? "primary.main" : "divider",
                      backgroundColor: courseTypes[type] ? "action.selected" : "transparent"
                    }}
                    control={
                      <Checkbox
                        checked={courseTypes[type]}
                        onChange={() => handleTypeChange(type)}
                        size="small"
                        sx={{ p: 0.25 }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                        {type}
                      </Typography>
                    }
                  />
                </Tooltip>
              ))}
            </Box>
          </Stack>

          {loading ? (
            <Box height={280} display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ px: 0, py: 0.5, maxHeight: 360, overflowY: "auto" }}>
              {filteredCourses.map((course) => {
                const isSelected = selected.includes(course.courseId);
                const isLimitReached =
  totalCourses >= 0 && usedCourses + selected.length >= totalCourses && !isSelected;
                return (
                  <Paper
                    key={course.courseId}
                    variant="outlined"
                    onClick={() => toggle(course.courseId)}
                    sx={{
                      mb: 1,
                      p: 1.5,
                      borderRadius: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.2,
                      borderColor: isSelected ? "primary.main" : "divider",
                      backgroundColor: isSelected ? "action.selected" : "background.paper",
                      transition: "border-color .18s ease, background .18s ease",
                      "&:hover": { borderColor: "primary.main" }
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          flex: "0 0 auto",
                          borderRadius: "10px",
                          display: "grid",
                          placeItems: "center",
                          color: isSelected ? "#fff" : "primary.main",
                          backgroundColor: isSelected ? "primary.main" : "action.selected"
                        }}
                      >
                        {isSelected ? <CheckCircleIcon fontSize="small" /> : <SchoolIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary", lineHeight: 1.25 }}>
                          {highlightText(course.courseName, debouncedSearch)}
                        </Typography>
                        {course.courseType && (
                          <Typography sx={{ mt: 0.25, fontSize: "0.75rem", fontWeight: 600, color: "text.secondary" }}>
                            {course.courseType}
                            {course.source === "organization" ? " · Your organization" : ""}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Checkbox checked={isSelected} disabled={isLimitReached} />
                  </Paper>
                );
              })}
              {!filteredCourses.length && (
                <Box sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: "12px", my: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                    No courses found
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "text.secondary" }}>
                    Try a different search or clear the filters.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </DialogContent>

        {/* ---------- Footer ---------- */}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${selected.length} selected`}
            color="primary"
            variant="outlined"
            sx={{ mr: "auto", fontWeight: 700 }}
          />
          <Button onClick={onClose} variant="outlined">Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selected.length || importing}
            onClick={() => setConfirmOpen(true)}
            startIcon={importing && <ButtonSpinner size={18} />}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Snackbar ---------- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type} variant="filled">
          {snackbar.msg}
        </Alert>
      </Snackbar>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ display: "block" }}>Import Options</Box>
            <Typography sx={{ mt: 0.25, fontSize: 12.5, fontWeight: 500, color: "text.secondary" }}>
              Choose how you want to import the selected courses
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <FormControl fullWidth>
            <RadioGroup value={importOption} onChange={(e) => setImportOption(e.target.value)}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: "12px",
                  borderColor: importOption === "ONLY_COURSES" ? "primary.main" : "divider",
                  backgroundColor: importOption === "ONLY_COURSES" ? "action.selected" : "background.paper"
                }}
              >
                <FormControlLabel
                  value="ONLY_COURSES"
                  control={<Radio />}
                  sx={{ m: 0, alignItems: "flex-start" }}
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}>
                        Course only
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "text.secondary" }}>
                        Content and materials — no trainings, no assessments
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: "12px",
                  borderColor: importOption === "COURSES_WITH_TRAININGS" ? "primary.main" : "divider",
                  backgroundColor: importOption === "COURSES_WITH_TRAININGS" ? "action.selected" : "background.paper"
                }}
              >
                <FormControlLabel
                  value="COURSES_WITH_TRAININGS"
                  control={<Radio />}
                  sx={{ m: 0, alignItems: "flex-start" }}
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}>
                        Course + trainings
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "text.secondary" }}>
                        Content and materials plus video trainings with their questions
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: "12px",
                  borderColor: importOption === "COURSES_WITH_ASSESSMENTS" ? "primary.main" : "divider",
                  backgroundColor: importOption === "COURSES_WITH_ASSESSMENTS" ? "action.selected" : "background.paper"
                }}
              >
                <FormControlLabel
                  value="COURSES_WITH_ASSESSMENTS"
                  control={<Radio />}
                  sx={{ m: 0, alignItems: "flex-start" }}
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}>
                        Course + assessments
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "text.secondary" }}>
                        Content and materials plus quizzes, exams and evaluations
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  borderColor: importOption === "COURSES_WITH_ASSESSMENTS_AND_TRAININGS" ? "primary.main" : "divider",
                  backgroundColor: importOption === "COURSES_WITH_ASSESSMENTS_AND_TRAININGS" ? "action.selected" : "background.paper"
                }}
              >
                <FormControlLabel
                  value="COURSES_WITH_ASSESSMENTS_AND_TRAININGS"
                  control={<Radio />}
                  sx={{ m: 0, alignItems: "flex-start" }}
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}>
                        Everything
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "text.secondary" }}>
                        Content, materials, trainings and all assessments
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </RadioGroup>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={importing}
            startIcon={importing && <ButtonSpinner size={18} />}
          >
            Confirm Import
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ImportCourse;
